import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Receipt, FileText, Building2,
} from 'lucide-react';
import { fmtINR } from '../components/invoicing/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const today = () => new Date().toISOString().slice(0, 10);
const fmtINR_localOverride_unused = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InvoiceFormPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const isEditing = !!invoiceId;

  const [config, setConfig] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  // Invoice form state
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [placeOfSupply, setPlaceOfSupply] = useState('');
  const [placeOfSupplyState, setPlaceOfSupplyState] = useState('');
  const [taxMode, setTaxMode] = useState('intra');     // intra | inter
  const [taxModeAuto, setTaxModeAuto] = useState(true); // auto-derive from POS state
  const [cgstPct, setCgstPct] = useState(9);
  const [sgstPct, setSgstPct] = useState(9);
  const [igstPct, setIgstPct] = useState(18);
  const [cessPct, setCessPct] = useState(0);
  const [terms, setTerms] = useState('');
  const [lineItems, setLineItems] = useState([{ description: '', sac_code: '', amount: '' }]);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invoicing/config`);
      setConfig(data);
    } catch (err) {
      console.warn('Config load failed', err);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const { data } = await axios.get(`${API}/invoicing/clients`);
      setClients(data.clients || []);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const fetchInvoice = useCallback(async () => {
    if (!invoiceId) return;
    setLoadingInvoice(true);
    try {
      const { data } = await axios.get(`${API}/invoicing/invoices/${invoiceId}`);
      if (data.status === 'paid') {
        toast.info('Paid invoices cannot be edited. Mark as Draft or Sent first.');
        navigate(`/invoices/${invoiceId}`, { replace: true });
        return;
      }
      setClientId(data.client_id);
      setIssueDate(data.issue_date || today());
      setDueDate(data.due_date || '');
      setPlaceOfSupply(data.place_of_supply || '');
      setPlaceOfSupplyState(data.place_of_supply_state || '');
      setTaxMode(data.tax_mode || 'intra');
      setTaxModeAuto(false); // editing existing — don't override their choice
      setCgstPct(data.cgst_percent ?? 9);
      setSgstPct(data.sgst_percent ?? 9);
      setIgstPct(data.igst_percent ?? 18);
      setCessPct(data.cess_percent ?? 0);
      setTerms(data.terms || '');
      setLineItems(
        (data.line_items && data.line_items.length > 0)
          ? data.line_items.map((li) => ({
              description: li.description || '',
              sac_code: li.sac_code || '',
              amount: String(li.amount ?? ''),
            }))
          : [{ description: '', sac_code: '', amount: '' }]
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoadingInvoice(false);
    }
  }, [invoiceId, navigate]);

  useEffect(() => { fetchConfig(); fetchClients(); }, [fetchConfig, fetchClients]);
  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // Auto-flip taxMode when POS state changes (only if user hasn't manually toggled)
  useEffect(() => {
    if (!taxModeAuto || !config) return;
    const home = config.org?.home_state;
    if (!placeOfSupplyState) return;
    setTaxMode(placeOfSupplyState === home ? 'intra' : 'inter');
  }, [placeOfSupplyState, taxModeAuto, config]);

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((acc, li) => acc + (parseFloat(li.amount) || 0), 0);
    const cgst = taxMode === 'intra' ? subtotal * (parseFloat(cgstPct) || 0) / 100 : 0;
    const sgst = taxMode === 'intra' ? subtotal * (parseFloat(sgstPct) || 0) / 100 : 0;
    const igst = taxMode === 'inter' ? subtotal * (parseFloat(igstPct) || 0) / 100 : 0;
    const cess = subtotal * (parseFloat(cessPct) || 0) / 100;
    const total = subtotal + cgst + sgst + igst + cess;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      igst: Math.round(igst * 100) / 100,
      cess: Math.round(cess * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  }, [lineItems, taxMode, cgstPct, sgstPct, igstPct, cessPct]);

  const addRow = () => setLineItems([...lineItems, { description: '', sac_code: '', amount: '' }]);
  const removeRow = (idx) => {
    if (lineItems.length === 1) {
      setLineItems([{ description: '', sac_code: '', amount: '' }]);
      return;
    }
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };
  const updateRow = (idx, key, value) => {
    setLineItems(lineItems.map((li, i) => (i === idx ? { ...li, [key]: value } : li)));
  };

  // When client is selected, auto-fill place of supply state from client state
  const onClientSelect = (cid) => {
    setClientId(cid);
    if (!taxModeAuto) return;
    const c = clients.find((x) => x.client_id === cid);
    if (c?.state) {
      setPlaceOfSupplyState(c.state);
      // Suggest a city-state label if not already typed
      if (!placeOfSupply) setPlaceOfSupply(c.state);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error('Please select a client'); return; }
    const cleaned = lineItems
      .map((li) => ({
        description: (li.description || '').trim(),
        sac_code: (li.sac_code || '').trim() || undefined,
        amount: parseFloat(li.amount),
      }))
      .filter((li) => li.description && !Number.isNaN(li.amount));
    if (cleaned.length === 0) {
      toast.error('Add at least one line item with description and amount');
      return;
    }

    const payload = {
      client_id: clientId,
      issue_date: issueDate,
      due_date: dueDate || null,
      place_of_supply: placeOfSupply || null,
      place_of_supply_state: placeOfSupplyState || null,
      tax_mode: taxMode,
      cgst_percent: parseFloat(cgstPct) || 0,
      sgst_percent: parseFloat(sgstPct) || 0,
      igst_percent: parseFloat(igstPct) || 0,
      cess_percent: parseFloat(cessPct) || 0,
      line_items: cleaned,
      terms: terms.trim() || null,
    };

    setSaving(true);
    try {
      let savedId;
      if (isEditing) {
        const { data } = await axios.put(`${API}/invoicing/invoices/${invoiceId}`, payload);
        savedId = data.invoice_id;
        toast.success('Invoice updated');
      } else {
        const { data } = await axios.post(`${API}/invoicing/invoices`, payload);
        savedId = data.invoice_id;
        toast.success(`Invoice ${data.invoice_number} created`);
      }
      navigate(`/invoices/${savedId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedClient = clients.find((c) => c.client_id === clientId);

  if (loadingInvoice) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <AppSidebar user={user} onLogout={logout} active="invoices" />
        <main className="ml-72 p-8 flex items-center justify-center h-screen">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoice...
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <AppSidebar user={user} onLogout={logout} active="invoices" />

      <main className="ml-72 p-8" data-testid="invoice-form-page">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/invoices')}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2"
            data-testid="back-to-invoices"
          >
            <ArrowLeft className="w-4 h-4" /> Back to invoices
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Receipt className="w-5 h-5 text-[#C9A84C]" />
            <span className="text-xs font-semibold text-[#C9A84C] uppercase tracking-[2px]">
              {isEditing ? 'Edit Proforma' : 'New Proforma Invoice'}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A2E]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            {isEditing ? 'Edit Proforma' : 'Create Proforma Invoice'}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            New invoices are issued as Proformas. A GST-compliant Tax Invoice will be auto-generated when you mark the proforma as paid.
          </p>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Main form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client + Dates + Place of Supply */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#1A1A2E]" />
                  Bill To, Dates &amp; Place of Supply
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="client-select">Client <span className="text-red-500">*</span></Label>
                  {clientsLoading ? (
                    <div className="text-sm text-slate-500 flex items-center gap-2 h-10">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading clients...
                    </div>
                  ) : clients.length === 0 ? (
                    <div className="text-sm text-slate-500 flex items-center justify-between gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <span>No clients yet. Create one first.</span>
                      <Button type="button" size="sm" onClick={() => navigate('/clients')}>
                        Manage Clients
                      </Button>
                    </div>
                  ) : (
                    <Select value={clientId} onValueChange={onClientSelect}>
                      <SelectTrigger id="client-select" data-testid="client-select-trigger">
                        <SelectValue placeholder="Select a client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id} data-testid={`client-option-${c.client_id}`}>
                            <span className="flex items-baseline gap-2">
                              <span className="font-medium">{c.name}</span>
                              {c.gstin && <span className="text-xs text-slate-400 font-mono">{c.gstin}</span>}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedClient && (
                    <div className="mt-2 text-xs text-slate-600 bg-[#FAF7F2] p-2.5 rounded border border-[#E0D8CC] space-y-0.5">
                      {selectedClient.client_code && (
                        <div className="font-mono text-[10px] font-semibold text-[#8B7220] uppercase tracking-wide mb-1">
                          Client Code: {selectedClient.client_code}
                        </div>
                      )}
                      {selectedClient.address && <div>{selectedClient.address}</div>}
                      {selectedClient.state && <div>{selectedClient.state}</div>}
                      {selectedClient.gstin && <div className="font-mono">GSTIN: {selectedClient.gstin}</div>}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="issue-date">Issue Date <span className="text-red-500">*</span></Label>
                    <Input id="issue-date" type="date" value={issueDate}
                           onChange={(e) => setIssueDate(e.target.value)} required
                           data-testid="issue-date-input" />
                  </div>
                  <div>
                    <Label htmlFor="due-date">Due Date</Label>
                    <Input id="due-date" type="date" value={dueDate}
                           onChange={(e) => setDueDate(e.target.value)}
                           data-testid="due-date-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pos-label">Place of Supply (Label)</Label>
                    <Input id="pos-label" value={placeOfSupply}
                           onChange={(e) => setPlaceOfSupply(e.target.value)}
                           placeholder="e.g. Raipur (Chhattisgarh)"
                           data-testid="place-of-supply-input" />
                  </div>
                  <div>
                    <Label htmlFor="pos-state">State (for tax inference)</Label>
                    <Select value={placeOfSupplyState} onValueChange={setPlaceOfSupplyState}>
                      <SelectTrigger id="pos-state" data-testid="pos-state-trigger">
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(config?.states || []).map((s) => (
                          <SelectItem key={s.code} value={s.name} data-testid={`pos-state-option-${s.code}`}>
                            <span className="flex items-baseline gap-2">
                              <span className="font-mono text-xs text-slate-400">{s.code}</span>
                              <span>{s.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line items */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Service Line Items</CardTitle>
                <Button type="button" size="sm" variant="outline" onClick={addRow}
                        data-testid="add-line-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Header row */}
                  <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-1">
                    <div className="col-span-7">Description</div>
                    <div className="col-span-2">SAC / HSN</div>
                    <div className="col-span-2 text-right">Amount (INR)</div>
                    <div className="col-span-1" />
                  </div>
                  {lineItems.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start"
                         data-testid={`line-item-${idx}`}>
                      <div className="col-span-7">
                        <Textarea
                          value={li.description}
                          onChange={(e) => updateRow(idx, 'description', e.target.value)}
                          placeholder="Description of services..."
                          rows={2}
                          data-testid={`line-item-desc-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={li.sac_code}
                          onChange={(e) => updateRow(idx, 'sac_code', e.target.value)}
                          placeholder={config?.default_sac || '998399'}
                          className="font-mono text-sm"
                          data-testid={`line-item-sac-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number" step="0.01" min="0"
                          value={li.amount}
                          onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="text-right"
                          data-testid={`line-item-amount-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(idx)}
                                className="hover:bg-red-50"
                                data-testid={`remove-line-item-${idx}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Default SAC <code className="font-mono">{config?.default_sac || '998399'}</code> applies when left blank — "Other professional, technical and business services".
                </p>
              </CardContent>
            </Card>

            {/* Tax configuration */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Tax Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => { setTaxMode('intra'); setTaxModeAuto(false); }}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                        taxMode === 'intra'
                          ? 'bg-white text-[#1A1A2E] shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid="tax-mode-intra"
                    >
                      Intra-state (CGST + SGST)
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTaxMode('inter'); setTaxModeAuto(false); }}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                        taxMode === 'inter'
                          ? 'bg-white text-[#1A1A2E] shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid="tax-mode-inter"
                    >
                      Inter-state (IGST)
                    </button>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={taxModeAuto}
                      onChange={(e) => setTaxModeAuto(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#C9A84C]"
                      data-testid="tax-mode-auto"
                    />
                    Auto-detect from Place-of-Supply state
                  </label>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {taxMode === 'intra' ? (
                    <>
                      <div>
                        <Label>CGST %</Label>
                        <Input type="number" step="0.01" min="0" max="100" value={cgstPct}
                               onChange={(e) => setCgstPct(e.target.value)}
                               data-testid="cgst-percent-input" />
                      </div>
                      <div>
                        <Label>SGST %</Label>
                        <Input type="number" step="0.01" min="0" max="100" value={sgstPct}
                               onChange={(e) => setSgstPct(e.target.value)}
                               data-testid="sgst-percent-input" />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label>IGST %</Label>
                      <Input type="number" step="0.01" min="0" max="100" value={igstPct}
                             onChange={(e) => setIgstPct(e.target.value)}
                             data-testid="igst-percent-input" />
                    </div>
                  )}
                  <div>
                    <Label>Cess %</Label>
                    <Input type="number" step="0.01" min="0" max="100" value={cessPct}
                           onChange={(e) => setCessPct(e.target.value)}
                           data-testid="cess-percent-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Terms */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Terms &amp; Conditions</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="Payment terms, bank details note, or any other conditions for the client..."
                  rows={3}
                  data-testid="terms-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right: Totals summary */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-[#1A1A2E] to-[#2E2E4A] text-white border-0 sticky top-6">
              <CardHeader className="pb-3 border-b border-white/10">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#C9A84C]" /> Live Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Subtotal</span>
                  <span className="font-semibold" data-testid="summary-subtotal">{fmtINR(totals.subtotal)}</span>
                </div>
                {taxMode === 'intra' ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">CGST @ {cgstPct}%</span>
                      <span data-testid="summary-cgst">{fmtINR(totals.cgst)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">SGST @ {sgstPct}%</span>
                      <span data-testid="summary-sgst">{fmtINR(totals.sgst)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">IGST @ {igstPct}%</span>
                    <span data-testid="summary-igst">{fmtINR(totals.igst)}</span>
                  </div>
                )}
                {totals.cess > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Cess @ {cessPct}%</span>
                    <span data-testid="summary-cess">{fmtINR(totals.cess)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-3 flex justify-between items-baseline">
                  <span className="text-sm font-medium text-slate-200">Total</span>
                  <span className="text-2xl font-bold text-[#C9A84C]" data-testid="summary-total">
                    {fmtINR(totals.total)}
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={saving || clients.length === 0}
                  className="w-full bg-[#C9A84C] hover:bg-[#B8972F] text-[#1A1A2E] font-semibold"
                  data-testid="save-invoice-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {isEditing ? 'Save Changes' : 'Create Invoice'}
                </Button>
                <Button
                  type="button" variant="outline"
                  className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/invoices')}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>

            {config?.org && (
              <Card className="bg-[#FAF7F2] border-[#E0D8CC]">
                <CardContent className="p-4 text-xs text-slate-600">
                  <p className="font-semibold text-[#1A1A2E] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>
                    {config.org.name}
                  </p>
                  <p className="text-[11px] text-[#C9A84C] mb-2">{config.org.tagline}</p>
                  <p className="font-mono text-[11px]">GSTIN: {config.org.gstin}</p>
                  <p className="font-mono text-[11px]">PAN: {config.org.pan}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default InvoiceFormPage;
